/* eslint-disable react-native/no-raw-text */
import JiriAccessory from '@/modules/jiri/JiriAccessory'
import JiriAccessoryBar from '@/modules/jiri/JiriAccessoryBar'
import { fetchCurrentUserThunk } from '@/redux/slices/currentUserSlice'
import { selectUnreadNotificationsCount } from '@/redux/slices/notificationSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { BlurTargetView, BlurView } from 'expo-blur'
import type { TabTriggerSlotProps } from 'expo-router/ui'
import { TabList, Tabs, TabSlot, TabTrigger } from 'expo-router/ui'
import { NativeTabs } from 'expo-router/unstable-native-tabs'
import JPush from 'jpush-react-native'
import { BellIcon, FileTextIcon, SearchIcon, TableIcon, UserIcon } from 'lucide-react-native'
import { useColorScheme } from 'nativewind'
import type { Ref } from 'react'
import { useEffect, useRef } from 'react'
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

function CustomTabButton({
  children,
  icon: Icon,
  isFocused,
  badge,
  ref,
  onPress,
  accessibilityRole,
}: TabTriggerSlotProps & {
  icon: typeof FileTextIcon
  /** A bare dot: this client only ever holds system notifications, never a mention. */
  badge?: boolean
  ref?: Ref<View>
}) {
  const { colorScheme } = useColorScheme()
  const isDark = colorScheme === 'dark'
  const activeColor = isDark ? '#0a84ff' : '#007aff'
  const inactiveColor = isDark ? '#f5f5f5' : '#1c1c1e'
  const color = isFocused ? activeColor : inactiveColor

  return (
    <Pressable ref={ref} onPress={onPress} accessibilityRole={accessibilityRole} style={styles.customTabButton}>
      <View>
        <Icon size={22} color={color} strokeWidth={isFocused ? 2.2 : 1.8} />
        {badge && <View style={styles.badge} />}
      </View>
      <Text style={[styles.customTabLabel, { color, fontWeight: isFocused ? '600' : '400' }]}>{children}</Text>
    </Pressable>
  )
}

function CustomTabs({ unreadCount, onTabFocus }: { unreadCount: number; onTabFocus: () => void }) {
  const blurTargetRef = useRef<View>(null)

  return (
    <Tabs
      style={{ flex: 1, overflow: 'visible' }}
      options={{
        screenListeners: {
          focus: () => {
            onTabFocus()
          },
        },
      }}
    >
      {Platform.OS === 'android' ? (
        <BlurTargetView ref={blurTargetRef} style={styles.customTabSlot}>
          <TabSlot style={{ flex: 1 }} />
        </BlurTargetView>
      ) : (
        <TabSlot style={styles.customTabSlot} />
      )}
      <CustomTabBar unreadCount={unreadCount} blurTargetRef={blurTargetRef} />
      <TabList style={{ display: 'none' }}>
        <TabTrigger name="forms" href="/forms">
          <Text>表单</Text>
        </TabTrigger>
        <TabTrigger name="tables" href="/tables">
          <Text>表格</Text>
        </TabTrigger>
        <TabTrigger name="notifications" href="/notifications">
          <Text>通知</Text>
        </TabTrigger>
        <TabTrigger name="search" href="/search">
          <Text>搜索</Text>
        </TabTrigger>
        <TabTrigger name="profile" href="/profile">
          <Text>我的</Text>
        </TabTrigger>
      </TabList>
    </Tabs>
  )
}

function CustomTabBar({
  unreadCount,
  blurTargetRef,
}: {
  unreadCount: number
  blurTargetRef: React.RefObject<View | null>
}) {
  const insets = useSafeAreaInsets()
  const { colorScheme } = useColorScheme()
  const jiriMobile = useAppSelector(state => state.session.jiriMobile)

  const tabButtons = (
    <>
      {jiriMobile && <JiriAccessoryBar />}
      <View style={styles.customTabRow}>
        <TabTrigger name="forms" asChild>
          <CustomTabButton icon={FileTextIcon}>表单</CustomTabButton>
        </TabTrigger>
        <TabTrigger name="tables" asChild>
          <CustomTabButton icon={TableIcon}>表格</CustomTabButton>
        </TabTrigger>
        <TabTrigger name="notifications" asChild>
          <CustomTabButton icon={BellIcon} badge={unreadCount > 0}>
            通知
          </CustomTabButton>
        </TabTrigger>
        <TabTrigger name="search" asChild>
          <CustomTabButton icon={SearchIcon}>搜索</CustomTabButton>
        </TabTrigger>
        <TabTrigger name="profile" asChild>
          <CustomTabButton icon={UserIcon}>我的</CustomTabButton>
        </TabTrigger>
      </View>
    </>
  )

  if (Platform.OS === 'android') {
    return (
      <BlurView
        nativeID="custom-tab-bar"
        tint={colorScheme === 'dark' ? 'dark' : 'light'}
        intensity={30}
        // expo-blur 55.0.11 fixed the initial radius to be divided by
        // blurReductionFactor (default 4), which weakened the blur 4x after the
        // SDK 57 upgrade. Factor 1 keeps the pre-upgrade frosted look.
        blurReductionFactor={1}
        blurMethod="dimezisBlurView"
        blurTarget={blurTargetRef}
        style={[
          styles.customTabBar,
          {
            paddingBottom: Math.max(insets.bottom, 8),
            borderColor: colorScheme === 'dark' ? 'rgba(28, 28, 30, 0.8)' : 'rgba(255, 255, 255, 0.8)',
          },
        ]}
      >
        {tabButtons}
      </BlurView>
    )
  }

  return (
    <View
      nativeID="custom-tab-bar"
      style={[
        styles.customTabBar,
        {
          paddingBottom: Math.max(insets.bottom, 8),
          borderColor: colorScheme === 'dark' ? 'rgba(28, 28, 30, 0.8)' : 'rgba(255, 255, 255, 0.8)',
        },
      ]}
    >
      {tabButtons}
    </View>
  )
}

function NativeTabsLayout({ unreadCount, onTabFocus }: { unreadCount: number; onTabFocus: () => void }) {
  const jiriMobile = useAppSelector(state => state.session.jiriMobile)

  return (
    <NativeTabs
      sidebarAdaptable
      minimizeBehavior="never"
      labelVisibilityMode="labeled"
      screenListeners={{
        focus: () => {
          onTabFocus()
        },
      }}
    >
      {jiriMobile && (
        <NativeTabs.BottomAccessory>
          <JiriAccessory />
        </NativeTabs.BottomAccessory>
      )}

      <NativeTabs.Trigger name="forms">
        <NativeTabs.Trigger.Icon sf={{ default: 'doc.text', selected: 'doc.text.fill' }} md="description" />
        <NativeTabs.Trigger.Label>表单</NativeTabs.Trigger.Label>
      </NativeTabs.Trigger>

      <NativeTabs.Trigger name="tables">
        <NativeTabs.Trigger.Icon sf={{ default: 'tablecells', selected: 'tablecells.fill' }} md="table_chart" />
        <NativeTabs.Trigger.Label>表格</NativeTabs.Trigger.Label>
      </NativeTabs.Trigger>

      <NativeTabs.Trigger name="notifications">
        <NativeTabs.Trigger.Icon sf={{ default: 'bell', selected: 'bell.fill' }} md="notifications" />
        <NativeTabs.Trigger.Label>通知</NativeTabs.Trigger.Label>
        {/* Childless, the badge is the bare red dot the system draws for a plain notice. */}
        <NativeTabs.Trigger.Badge hidden={unreadCount <= 0} />
      </NativeTabs.Trigger>

      <NativeTabs.Trigger name="search" role="search">
        <NativeTabs.Trigger.Icon sf="magnifyingglass" md="search" />
        <NativeTabs.Trigger.Label>搜索</NativeTabs.Trigger.Label>
      </NativeTabs.Trigger>

      <NativeTabs.Trigger name="profile">
        <NativeTabs.Trigger.Icon sf={{ default: 'person', selected: 'person.fill' }} md="person" />
        <NativeTabs.Trigger.Label>我的</NativeTabs.Trigger.Label>
      </NativeTabs.Trigger>
    </NativeTabs>
  )
}

export default function TabsLayout() {
  const dispatch = useAppDispatch()
  const unreadCount = useAppSelector(selectUnreadNotificationsCount)

  useEffect(() => {
    JPush.setBadge({ badge: unreadCount, appBadge: unreadCount })
  }, [unreadCount])

  const onTabFocus = () => {
    dispatch(fetchCurrentUserThunk())
  }

  if (Platform.OS === 'web' || Platform.OS === 'android') {
    return <CustomTabs unreadCount={unreadCount} onTabFocus={onTabFocus} />
  }

  return <NativeTabsLayout unreadCount={unreadCount} onTabFocus={onTabFocus} />
}

const styles = StyleSheet.create({
  customTabSlot: {
    flex: 1,
  },
  customTabBar: {
    position: 'absolute' as const,
    bottom: 0,
    left: 0,
    right: 0,
    paddingTop: 10,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    overflow: 'hidden',
  },
  customTabRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  customTabButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 4,
    gap: 4,
  },
  customTabLabel: {
    fontSize: 12,
  },
  badge: {
    position: 'absolute',
    top: -2,
    right: -6,
    width: 8,
    height: 8,
    backgroundColor: '#ff3b30',
    borderRadius: 4,
  },
})
